/**
 * Deal Dossier — Economics dashboard.
 *
 * Faithful to the approved institutional economics layout: nine-KPI rail,
 * four-panel top row, three-panel middle row, and four-panel bottom row.
 * Every value comes from the deal record, stored schedules, activity/notes,
 * or the loan calculation service. Missing schedules keep their visual slot
 * and state plainly that no series is recorded.
 */

import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  ArrowRight,
  Banknote,
  FileText,
  LineChart as LineChartIcon,
  Percent,
} from 'lucide-react'
import {
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import { holdsSponsorFigure, sponsorFigure, type RawDealFile, type DealFieldDef } from '@/services/dealFile'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import type { LoanActivityEvent, LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { FIELD_STATE_TOKENS } from '@/components/fields/fieldState'
import { lineageSummary, stateOfLineage } from '@/components/fields/lineage'
import type { DealDossierTab } from '@/store/dealDossierStore'
import { isDerivedDateField } from './derivedDates'
import { DossierPanel, EmptyLine } from './ui'
import type { EditTarget } from './editableFields'
import { EditableValue, showsStandIn } from './EditableValue'
import { FinancialChart } from './FinancialChart'
import { SponsorStatementChart } from './SponsorStatementChart'
import type { SponsorStatement } from './economicsModel'
import { useDossierEdit } from './editContext'
import { useProvideDossierMetrics, type DossierMetric } from './MetricBar'
import { formatAmount, formatMultiple, formatPct, formatPoints } from './format'
import { ltvWithImplied } from './valueBasis'
import { calculatedFrom } from './leverageModel'
import { StatedFiguresBlock } from './StatedFiguresBlock'
import { ValueStateChip } from './valueStateChip'
import type { FieldSourceRef } from './workspace/types'
import type { CoverageRowId } from './economicsCoverage'
import {
  buildEconomicsModel,
  financingDisplay,
  scenarioLtvText,
  type ScenarioId,
  type ScenarioMetrics,
  type ScenarioValueBasis,
  type SensitivityRow,
} from './economicsModel'

const GOLD = '#d9b768'
const EXPENSE_COLORS = ['#5f8f78', '#64706f', '#9e8857', '#c8ad70', '#786a55', '#8a7398', '#a47c84', '#c66f62']

function moneyFull(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const negative = value < 0
  const absolute = Math.abs(value)
  const body = absolute >= 1_000_000
    ? `$${(absolute / 1_000_000).toFixed(absolute % 1_000_000 === 0 ? 0 : 2)}M`
    : `$${Math.round(absolute).toLocaleString()}`
  return negative ? `(${body})` : body
}

function when(iso: string): string {
  if (!iso) return 'Date not recorded'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Percent fields keep 0-100 unless a real loan column holds them; `ltv` is the
 *  only one that does, and it stores a fraction. */
const CUSTOM_SCALE: Pick<DealFieldDef, 'native' | 'kind'> = { native: false, kind: 'number' }
const NATIVE_PCT: Pick<DealFieldDef, 'native' | 'kind'> = { native: true, kind: 'percent' }


function PanelLink({ label, onClick }: { label: string; onClick?: () => void }) {
  if (!onClick) return null
  return (
    <button type="button" onClick={onClick} className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-amber-300/90 hover:text-amber-200">
      {label} <ArrowRight size={10} />
    </button>
  )
}

function ChartEmpty({ children, minHeight = 160 }: { children: ReactNode; minHeight?: number }) {
  // The minimum holds on narrow screens; on the single-screen desktop grid the
  // empty state takes whatever its panel has, so it never forces a scroll.
  return (
    <div
      className="flex min-h-[var(--chart-empty-min)] flex-1 items-center justify-center px-5 xl:min-h-0"
      style={{ '--chart-empty-min': `${minHeight}px` } as CSSProperties}
    >
      <EmptyLine>{children}</EmptyLine>
    </div>
  )
}

function CashOnCashGauge({ value }: { value: number | undefined }) {
  const percent = value == null ? 0 : Math.max(0, Math.min(20, value))
  const dial = (percent / 20) * 100
  return (
    // shrink-0: in the panel's flex column the ring shrank to a sliver while
    // its centred label stayed full size, over the IRR rows below (nd-074).
    <div className="relative mx-auto h-28 w-28 shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="72%" outerRadius="100%" startAngle={210} endAngle={-30} data={[{ name: 'Cash on Cash', value: dial }]}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar dataKey="value" cornerRadius={999} background={{ fill: 'rgba(255,255,255,0.06)' }} fill={value == null ? '#292524' : GOLD} isAnimationActive={false} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-2">
        <div className={`font-mono text-[20px] font-semibold ${value == null ? 'text-stone-600' : 'text-stone-100'}`}>
          {value == null ? '—' : formatPct(value)}
        </div>
        <div className="font-mono text-[8px] uppercase tracking-[0.14em] text-stone-500">Cash on Cash</div>
      </div>
    </div>
  )
}

type ScenarioRow = {
  label: string
  /** The figure this row shows for one scenario; undefined reads as a dash. */
  read: (scenario: ScenarioMetrics) => number | undefined
  format: 'money' | 'multiple' | 'ltv' | 'percent'
}

function valueOnBasis(basis: ScenarioValueBasis) {
  return (scenario: ScenarioMetrics) => (scenario.valueBasis === basis ? scenario.value : undefined)
}

const VALUE_ROWS: { basis: ScenarioValueBasis; label: string }[] = [
  { basis: 'as_is', label: 'Value (As-Is)' },
  { basis: 'stabilized', label: 'Value (Stabilized)' },
  { basis: 'unstated', label: 'Value (basis not stated)' },
]

/** The Scenario Analysis rows. Each row names one basis and shows only
 *  figures stated on it: an unqualified NOI, DSCR or value gets a row of its
 *  own instead of borrowing the stabilized or as-is heading (C09). A row for a
 *  basis no scenario states is left out, except the headline ones. */
function scenarioRowsFor(scenarios: (ScenarioMetrics | null)[]): ScenarioRow[] {
  const present = scenarios.filter((scenario): scenario is ScenarioMetrics => Boolean(scenario))
  const any = (read: ScenarioRow['read']) => present.some((scenario) => read(scenario) != null)
  const valueRows: ScenarioRow[] = VALUE_ROWS.filter(({ basis }) => any(valueOnBasis(basis))).map(
    ({ basis, label }) => ({ label, read: valueOnBasis(basis), format: 'money' }),
  )
  const rows: ScenarioRow[] = [{ label: 'NOI (Stabilized)', read: (s) => s.noi, format: 'money' }]
  if (any((s) => s.noiUnqualified)) {
    rows.push({ label: 'NOI (basis not stated)', read: (s) => s.noiUnqualified, format: 'money' })
  }
  rows.push(...(valueRows.length ? valueRows : [{ label: 'Value', read: () => undefined, format: 'money' as const }]))
  rows.push({ label: 'LTV', read: (s) => s.ltv, format: 'ltv' })
  rows.push({ label: 'DSCR (Stabilized)', read: (s) => s.dscr, format: 'multiple' })
  if (any((s) => s.dscrUnqualified)) {
    rows.push({ label: 'DSCR (basis not stated)', read: (s) => s.dscrUnqualified, format: 'multiple' })
  }
  rows.push({ label: 'Cash on Cash', read: (s) => s.cashOnCash, format: 'percent' })
  rows.push({ label: 'IRR (Levered)', read: (s) => s.irr, format: 'percent' })
  return rows
}

function scenarioValue(scenario: ScenarioMetrics | null, row: ScenarioRow): string {
  const value = scenario ? row.read(scenario) : undefined
  switch (row.format) {
    case 'ltv':
      return scenarioLtvText(scenario)
    case 'money':
      return moneyFull(value)
    case 'multiple':
      return formatMultiple(value)
    case 'percent':
      return formatPct(value)
    default: {
      const _exhaustive: never = row.format
      return _exhaustive
    }
  }
}

function sensitivityValue(row: SensitivityRow, value: number | undefined): string {
  if (value == null) return '—'
  const unit = row.unit?.toLowerCase() ?? ''
  const label = row.label.toLowerCase()
  if (unit.includes('$') || unit.includes('money') || /noi|value|income/.test(label)) return moneyFull(value)
  if (unit.includes('%') || unit.includes('percent') || /cap|irr|rate/.test(label)) return formatPct(value)
  if (unit.includes('month')) return `${value} mo.`
  if (unit.includes('year') || /hold/.test(label)) return `${value} yr.`
  return value.toLocaleString()
}

/**
 * A sensitivity result, formatted by what the result measures.
 *
 * The row's `unit` describes the input being varied (vacancy in %, SOFR in %),
 * not the answer. A DSCR answer formatted with the input's "%" printed a 1.21x
 * coverage as "121%". The result label says what the output is.
 */
function sensitivityResultValue(row: SensitivityRow): string {
  const value = row.result
  if (value == null) return '—'
  const label = (row.resultLabel ?? '').toLowerCase()
  // Whole words: "cap" must not match "Capital required", nor "rate" match
  // "Generated cash".
  if (/\b(dscr|coverage|multiple|x)\b/.test(label)) return formatMultiple(value)
  if (/\b(irrs?|yields?|rates?|ltv|ltc|cap|returns?|margins?|occupancy)\b|%/.test(label)) return formatPct(value)
  if (/\b(noi|values?|proceeds|income|cash flow)\b|\$/.test(label)) return moneyFull(value)
  return sensitivityValue(row, value)
}

type EconomicsModel = ReturnType<typeof buildEconomicsModel>

/** The chip source of a figure computed rather than held: this page's own
 *  arithmetic, or the calculation service's output shown while the deal holds
 *  no value for the field. */
function computedSource(fieldName: string, formula: string | null | undefined): FieldSourceRef {
  const said = formula || 'computed from other fields'
  return {
    label: `Derived: ${said}`,
    detail: null,
    fieldName,
    attribution: null,
    cellRef: null,
    disputed: false,
    state: 'derived',
    formula: said,
  }
}

/** The catalogue field a summary value is, for editing it in place. */
type SummaryField = {
  name: string
  label: string
  fields?: EditTarget[]
  inputs?: string[]
  /** The row's figure is computed here whatever the field holds (the
   *  interest-only coverage): its formula. */
  computed?: string
}

/** A summary value with its value state before it: the figure truncates
 *  first, the chip never does.
 *
 *  Given the field it shows, the value is editable in place once the dossier
 *  is unlocked (owner, 21 Oct): a stated figure becomes the dossier's input in
 *  the same right-aligned place, and a computed one — this page's arithmetic,
 *  the calculation service's figure for an empty field, or an engine output —
 *  opens its inputs instead. Locked, it is exactly the value and chip it was. */
function StatedValue({
  value,
  source,
  className = 'text-stone-200',
  field,
}: {
  value: ReactNode
  source: FieldSourceRef | null
  className?: string
  field?: SummaryField
}) {
  const edit = useDossierEdit()
  const chip = source ? <span className="shrink-0"><ValueStateChip source={source} /></span> : null
  // A figure shown while the field itself holds nothing (read off the deal's
  // schedule, or the calculation service's) is not the field's value: an
  // input there would blank the figure on screen, so it reads as computed.
  // A derived date (Maturity, Extension Maturity) is the exception: typing it
  // states it, so it is an input and not an output that opens its own inputs.
  const computed =
    source?.state === 'derived' && !isDerivedDateField(field?.name ?? '')
      ? source.formula ?? ''
      : field?.computed !== undefined
        ? field.computed
        : field && !field.fields && typeof value === 'string' && showsStandIn(edit, field.name, value)
        ? 'read from the deal’s schedule, while the field itself is empty'
        : undefined
  const locked = (
    <>
      {chip}
      <span className="min-w-0 truncate">{value}</span>
    </>
  )
  return (
    <dd className={`flex min-w-0 items-center justify-end gap-1 text-right font-mono ${className}`}>
      {field ? (
        <EditableValue
          name={field.name}
          label={field.label}
          display={value}
          marks={chip}
          marksFirst
          fields={field.fields}
          computed={computed}
          inputs={field.inputs}
        >
          {locked}
        </EditableValue>
      ) : (
        locked
      )}
    </dd>
  )
}

/** Catalogue field behind a coverage row, so a derived broker-stated DSCR
 *  (or the deal's DSCR when that is what the row shows) carries its chip. */
function coverageCatalogueField(id: CoverageRowId, held: (name: string) => unknown): string | undefined {
  switch (id) {
    case 'dscr_io':
      return 'dscr_io'
    case 'dscr_io_stabilized':
      return 'dscr_io_stabilized'
    case 'dscr_stated': {
      const stated = held('dscr_stated')
      if (stated !== null && stated !== undefined && stated !== '') return 'dscr_stated'
      return 'dscr'
    }
    case 'dscr_hotel_after_ffe':
      return undefined
    default: {
      const _exhaustive: never = id
      return _exhaustive
    }
  }
}

/** The economics model the Economics tab reads, built the same way wherever it
 *  is shown: the Overview carries the same cash-flow summary. */
export function useEconomicsModel({
  raw,
  loanId,
  activity,
  notes,
  operatingHistory,
}: {
  raw: RawDealFile
  loanId: string
  activity: LoanActivityEvent[]
  notes: { id: string; body: string; author: string; createdAt: string }[]
  operatingHistory?: Record<string, unknown>[] | null
}): EconomicsModel {
  const { effectiveValue } = useDossierEdit()
  const calculations = useQuery({
    queryKey: LOAN_QUERY_KEYS.calculations(loanId),
    queryFn: () => loansService.getCalculations(loanId),
    enabled: Boolean(loanId),
    retry: false,
    refetchInterval: 30_000,
  })

  const get = useCallback((name: string) => {
    const stated = effectiveValue(name)
    if (stated !== null && stated !== undefined && stated !== '') return stated
    // By catalogue name OR its camelCase spelling: the service camelises the
    // payload's keys, so the engine's `debt_yield` arrives as `debtYield` and
    // a lookup by the catalogue name alone never found it (calculatedFrom).
    const derived = calculatedFrom(calculations.data?.derived)(name)?.value
    return typeof derived === 'number' && Number.isFinite(derived) ? derived : stated
  }, [calculations.data, effectiveValue])

  return useMemo(
    () => buildEconomicsModel({ get, raw, activity, notes, operatingHistory }),
    [get, raw, activity, notes, operatingHistory],
  )
}

/** The grid is titled by its own columns as the workbook labels them — the
 *  panel once hard-coded one client's firm name into every tenant's title. */
export function cashFlowSummaryTitle(model: EconomicsModel): string {
  if (!model.uwGrid) return 'Cash Flow Summary (In-Place)'
  const labels = model.uwGrid.columns.map((column) => column.label).filter(Boolean)
  return labels.length ? `Cash Flow (${labels.join(' / ')})` : 'Cash Flow (Underwriting Grid)'
}

/** The cash-flow lines (or the sponsor's UW grid when one was read). `dense`
 *  tightens the rows for the Overview, where it shares a column. */
/** The state chip for a cash-flow line that shows a catalogue field the
 *  platform derived, suggested or a person confirmed. Stated lines stay bare,
 *  as on the Overview tables. Westbrook's debt service is NOI / DSCR and read
 *  as plain fact (OVR-05). */
function CashFlowLineState({ lineage }: { lineage?: LoanFieldLineageField }) {
  if (!lineage) return null
  const state = stateOfLineage(lineage)
  if (state !== 'derived' && state !== 'suggested' && state !== 'confirmed') return null
  const token = FIELD_STATE_TOKENS[state]
  const formula = lineage.derivation?.formula
  return (
    <span
      data-value-state={state}
      title={state === 'derived' && formula ? `Derived: ${formula}` : lineageSummary(lineage) || token.description}
      className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${token.className}`}
    >
      {token.label}
    </span>
  )
}

export function CashFlowSummaryContent({
  model,
  dense = false,
  lineage = [],
}: {
  model: EconomicsModel
  dense?: boolean
  /** The deal's field lineage, for the state of the lines that are fields. */
  lineage?: LoanFieldLineageField[]
}) {
  const edit = useDossierEdit()
  const cashFlowHasData = model.cashFlowLines.some((line) => line.value != null)
  const lineageOf = (name: string | undefined) =>
    name ? lineage.find((field) => field.fieldName === name) : undefined
  return (
    <>
      {model.hotelStats ? (
        <div
          data-testid="hotel-operating-stats"
          className="mb-1.5 grid grid-cols-[repeat(auto-fit,minmax(3.25rem,1fr))] gap-1 rounded border border-white/[0.05] bg-black/20 px-1.5 py-1"
        >
          {model.hotelStats.map((stat) => (
            <div key={stat.fieldName} className="flex min-w-0 flex-col justify-between">
              <div className="font-mono text-[8px] uppercase leading-tight tracking-wider text-stone-500">{stat.label}</div>
              <div className="truncate font-mono text-[10px] text-stone-200" title={stat.value}>
                <EditableValue
                  name={stat.fieldName}
                  label={stat.label}
                  display={stat.value}
                  computed={showsStandIn(edit, stat.fieldName, stat.value) ? 'the calculation service, while the deal states none' : undefined}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {model.uwGrid ? (
        <div data-testid="uw-cash-flow-grid">
          <div className="mb-1 truncate font-mono text-[8px] uppercase tracking-widest text-amber-200/60">
            {model.uwGrid.workbook} · {model.uwGrid.sheet}
          </div>
          <div className="max-h-[240px] overflow-auto">
            <table className="w-full text-left text-[10px]">
              <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[8px] uppercase tracking-wider text-stone-600">
                <tr>
                  <th className="px-1 py-1 font-normal">Line</th>
                  {model.uwGrid.columns.map((column) => (
                    <th key={column.id} className="px-1 py-1 text-right font-normal">{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.uwGrid.lines.map((line) => (
                  <tr key={`${line.key ?? line.label}`} className="border-t border-white/[0.04] text-stone-300">
                    <td className={`px-1 py-0.5 ${line.key ? 'text-amber-100/80' : ''}`} title={Object.values(line.cells).filter(Boolean).join(' · ') || undefined}>
                      {line.label}
                    </td>
                    {model.uwGrid!.columns.map((column) => (
                      <td key={column.id} className="px-1 text-right font-mono" title={line.cells[column.id]}>
                        {moneyFull(line.values[column.id] ?? undefined)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-0.5">
            {model.cashFlowLines.map((line) => (
              <div key={line.label} className={`flex items-center justify-between gap-2 rounded px-1.5 ${dense ? 'py-0.5 leading-tight' : 'py-1'} text-[10px] ${line.emphasize ? 'border border-amber-300/20 bg-amber-300/[0.06] font-medium' : ''}`}>
                <span className={`flex items-center gap-1 ${line.emphasize ? 'text-amber-200' : 'text-stone-400'}`}>
                  {line.label}
                  {line.value != null && line.suggested ? (
                    <span
                      data-value-state="suggested"
                      title={line.suggested}
                      className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${FIELD_STATE_TOKENS.suggested.className}`}
                    >
                      {FIELD_STATE_TOKENS.suggested.label}
                    </span>
                  ) : line.value != null && line.derived ? (
                    // A line this page computes from the lines above (C05).
                    <span
                      data-value-state="derived"
                      title={`Derived: ${line.derived}`}
                      className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${FIELD_STATE_TOKENS.derived.className}`}
                    >
                      {FIELD_STATE_TOKENS.derived.label}
                    </span>
                  ) : line.value != null ? <CashFlowLineState lineage={lineageOf(line.fieldName)} /> : null}
                </span>
                <span className={`shrink-0 font-mono ${line.expense && line.value != null ? 'text-red-300' : line.emphasize ? 'text-amber-100' : 'text-stone-200'}`}>
                  {/* A line that IS a catalogue field is that field, edited in
                      place when unlocked; a line this page computes opens the
                      fields it is made of (the reserve, the debt service).
                      A line read off the cash-flow schedule (vacancy, loss to
                      lease) is no field and stays as it reads. */}
                  {line.fieldName || ((line.derived || line.suggested) && line.inputs?.length && line.value != null) ? (
                    <EditableValue
                      name={line.fieldName ?? `cash_flow_line:${line.label}`}
                      label={line.label}
                      display={moneyFull(line.value)}
                      computed={
                        line.value == null
                          ? undefined
                          : line.derived
                            ?? (line.suggested ? line.suggested.replace(/^Suggested:\s*/, '') : undefined)
                            // The calculation service's figure for a field the
                            // deal leaves empty: an input over it would blank it.
                            ?? (line.fieldName && showsStandIn(edit, line.fieldName, moneyFull(line.value))
                              ? 'the calculation service, while the deal states none'
                              : undefined)
                      }
                      inputs={line.inputs}
                    />
                  ) : (
                    moneyFull(line.value)
                  )}
                </span>
              </div>
            ))}
          </div>
          {!cashFlowHasData ? <p className="mt-1 text-[9px] text-stone-600">No cash-flow summary values are recorded.</p> : null}
        </>
      )}
    </>
  )
}

/** The debt service the in-place DSCR divides by, as the cash-flow summary
 *  carries it (an outflow, so negative there). */
function annualDebtService(lines: { label: string; value?: number | null }[]): number | undefined {
  const line = lines.find((l) => l.label === 'Debt Service')
  return line?.value == null ? undefined : Math.abs(line.value)
}

/**
 * The catalogue fields behind the Financing Summary rows whose label is not
 * simply the field they show (LPR-16): the catalogue's own label for the input,
 * the name the server's 409 / 422 texts use, and the right field where the row
 * reads a fallback. One row can stand for two fields — "Amortization" shows
 * the type and the months as one label, and edits them as the two fields they
 * are. Any other row edits the field it names; a derived one opens its inputs.
 */
export const FINANCING_EDITORS: Record<string, EditTarget[]> = {
  'Loan Amount': [{ name: 'loan_amount', label: 'Loan Amount', kind: 'currency' }],
  Position: [{ name: 'position', label: 'Position', kind: 'select' }],
  'Loan Type': [{ name: 'loan_type', label: 'Product / Loan Type', kind: 'text' }],
  'Rate Type': [{ name: 'rate_type', label: 'Rate Type', kind: 'select' }],
  'Interest Rate': [{ name: 'rate', label: 'Interest Rate', kind: 'text' }],
  'Day Count': [{ name: 'day_count', label: 'Day Count', kind: 'select' }],
  'Index Rate Type': [{ name: 'index', label: 'Index Rate Type', kind: 'select' }],
  Spread: [{ name: 'spread', label: 'Spread', kind: 'text' }],
  Floor: [{ name: 'floor', label: 'Floor', kind: 'percent' }],
  'Payment Frequency': [{ name: 'payment_frequency', label: 'Payment Frequency', kind: 'select' }],
  Amortization: [
    { name: 'amortization_type', label: 'Amortization Type', kind: 'select' },
    { name: 'amortization_months', label: 'Amortization (months)', kind: 'number', unit: 'mo' },
  ],
  'Closing Date': [{ name: 'closing_date', label: 'Closing Date', kind: 'date' }],
  'Loan Term': [{ name: 'term_months', label: 'Loan Term (months)', kind: 'number' }],
  // Maturity and Extension Maturity are stated by a package or a person, else
  // derived from the closing date and the term. A date a person types over the
  // derived one is a statement and governs (`DERIVED_DATE_FIELDS`).
  Maturity: [{ name: 'maturity_date', label: 'Maturity', kind: 'date' }],
  'IO Period': [{ name: 'io_months', label: 'IO Period (months)', kind: 'number' }],
  'Extension Options': [{ name: 'extension_count', label: 'Extension Options', kind: 'count' }],
  Extensions: [{ name: 'extensions', label: 'Extensions (months)', kind: 'number' }],
  'Extension Maturity': [{ name: 'extension_maturity_date', label: 'Extension Maturity', kind: 'date' }],
  'Origination Fee': [{ name: 'origination_fee_bps', label: 'Origination Fee (bps)', kind: 'number' }],
  'Exit Fee': [{ name: 'exit_fee_bps', label: 'Exit Fee (bps)', kind: 'number' }],
  Prepayment: [{ name: 'prepayment', label: 'Prepayment', kind: 'text' }],
  'Value (Stabilized)': [{ name: 'value_stabilized', label: 'Value (Stabilized)', kind: 'currency' }],
}

export function EconomicsPage({
  raw,
  loanId,
  activity,
  notes,
  operatingHistory,
  onGoto,
  canGoto,
}: {
  raw: RawDealFile
  defs: DealFieldDef[]
  loanId: string
  activity: LoanActivityEvent[]
  notes: { id: string; body: string; author: string; createdAt: string }[]
  /** The linked property master's measured operating history, if any. */
  operatingHistory?: Record<string, unknown>[] | null
  onGoto?: (tab: DealDossierTab) => void
  /** Whether this reader may open a tab; a link into one they may not is not
   *  drawn (the Step-1 analyst has no Underwriting tab). Absent: every tab. */
  canGoto?: (tab: DealDossierTab) => boolean
}) {
  const gotoLink = (tab: DealDossierTab) =>
    onGoto && (canGoto?.(tab) ?? true) ? () => onGoto(tab) : undefined
  /** The sponsor's figure behind a KPI, when the card is in fact showing it.
   *
   *  Compared against the value on screen rather than trusted from the deal
   *  record, because a KPI can be derived: stabilized DSCR is computed from
   *  the stabilized NOI, so the card is the engine's arithmetic, not the
   *  sponsor's assertion, and must not carry their name. */
  const statedBySponsor = useCallback(
    (name: string, shown: number | null | undefined, scale = CUSTOM_SCALE) => {
      const figure = sponsorFigure(raw, name)
      return figure && holdsSponsorFigure(scale, shown, figure) ? figure : undefined
    },
    [raw],
  )
  const { effectiveValue, valueSource } = useDossierEdit()
  // The same cached read `useEconomicsModel` makes: a figure it shows from
  // the calculation service, while the deal holds none, is Derived.
  const calculations = useQuery({
    queryKey: LOAN_QUERY_KEYS.calculations(loanId),
    queryFn: () => loansService.getCalculations(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })
  /**
   * The value state of a summary figure (C05). A catalogue field's own value
   * carries its lineage state, exactly as an EditableSlot shows it; a figure
   * computed here, or by the calculation service because the field is empty,
   * reads Derived with its formula. Stated figures draw no chip.
   */
  const stateOf = useCallback(
    (fieldName: string | undefined, pageFormula?: string): FieldSourceRef | null => {
      if (pageFormula) return computedSource(fieldName ?? '', pageFormula)
      if (!fieldName) return null
      const held = effectiveValue(fieldName)
      if (held !== null && held !== undefined && held !== '') return valueSource?.(fieldName) ?? null
      const computed = calculatedFrom(calculations.data?.derived)(fieldName)
      return computed ? computedSource(fieldName, computed.formula) : null
    },
    [calculations.data, effectiveValue, valueSource],
  )
  const [returnsMode, setReturnsMode] = useState<'inplace' | 'stabilized'>('inplace')
  const [selectedScenario, setSelectedScenario] = useState<ScenarioId>('base')

  const model = useEconomicsModel({ raw, loanId, activity, notes, operatingHistory })
  // The same cached lineage read the dossier makes, for the cash-flow lines'
  // value states.
  const lineageQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId),
    queryFn: () => loansService.getFieldLineage(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })
  const expenseData = model.expenses.map((expense) => ({ name: expense.name, value: expense.amount }))
  const scenarioIds: ScenarioId[] = ['downside', 'base', 'upside']
  const scenarioRows = scenarioRowsFor(scenarioIds.map((id) => model.scenarios[id]))
  const currentCashOnCash = returnsMode === 'inplace'
    ? model.returns.cashOnCash
    : model.returns.cashOnCashStabilized

  /**
   * The figures this page contributes to the dossier's one metric bar.
   *
   * Each names a header slot the bar already carries (loan amount, LTV, debt
   * yield, DSCR in-place and stabilized), so it merges onto that slot rather
   * than repeating it, and lends what it is made of, so the tile's dialog can
   * answer "where did that come from" without sending the reader to another
   * tab. NOI, value, LTC, cash on cash and IRR are read in this page's own
   * panels: the bar is one definition on every tab (Chris Young, 2 Nov).
   */
  const metrics = useMemo<DossierMetric[]>(
    () => [
      { id: 'loan_amount', label: 'Loan Amount', value: formatAmount(model.kpis.loanAmount), icon: Banknote, sponsor: statedBySponsor('loan_amount', model.kpis.loanAmount) },
      {
        id: 'ltv',
        label: 'LTV',
        // The recorded LTV, and the one the loan and value imply beside it
        // when they differ (R3 keeps the stated ratio; R4 shows the gap).
        value: ltvWithImplied(model.kpis.ltv, model.kpis.loanAmount, model.kpis.propertyValue),
        icon: Percent,
        sponsor: statedBySponsor('ltv', model.kpis.ltv, NATIVE_PCT),
        note: 'Loan amount against the as-is value of the asset.',
        detail: [
          { label: 'Loan amount', value: formatAmount(model.kpis.loanAmount) },
          { label: 'Value (as-is)', value: formatAmount(model.kpis.propertyValue) },
        ],
      },
      {
        id: 'dscr',
        label: 'DSCR (In-Place)',
        value: formatMultiple(model.kpis.dscr),
        icon: LineChartIcon,
        sponsor: statedBySponsor('dscr', model.kpis.dscr),
        note: 'In-place net operating income against annual debt service (NOI / debt service).',
        // The two operands of THIS figure. It listed the stabilized DSCR in
        // place of the debt service it divides by (ECON-21).
        detail: [
          { label: 'NOI (in-place)', value: formatAmount(model.kpis.noi) },
          { label: 'Annual debt service', value: formatAmount(annualDebtService(model.cashFlowLines)) },
        ],
        series: model.dscrSeries.map((point) => ({ label: point.label, value: point.dscr ?? null })),
        seriesFormat: (value: number) => `${value.toFixed(2)}x`,
      },
      {
        id: 'debt_yield',
        label: 'Debt Yield',
        value: model.kpis.debtYieldUnreadable ? 'n/a' : formatPoints(model.kpis.debtYield),
        fieldValue: !model.kpis.debtYieldUnreadable,
        icon: Percent,
        sponsor: statedBySponsor('debt_yield', model.kpis.debtYield),
        note: model.kpis.debtYieldUnreadable
          ? 'This note sits behind senior debt the record does not state, so its debt yield is not shown on the note alone.'
          : 'In-place net operating income against the loan amount.',
        detail: [
          { label: 'In-place', value: model.kpis.debtYieldUnreadable ? 'n/a' : formatPoints(model.kpis.debtYield) },
          { label: 'Stabilized', value: model.kpis.debtYieldUnreadable ? 'n/a' : formatPoints(model.kpis.debtYieldStabilized) },
        ],
      },
      { id: 'dscr_stabilized', fieldValue: true, label: 'DSCR (Stabilized)', value: formatMultiple(model.kpis.dscrStabilized), icon: Activity, sponsor: statedBySponsor('dscr_stabilized', model.kpis.dscrStabilized) },
    ],
    [model, statedBySponsor],
  )
  // A figure the deal does not hold, shown because the calculation service
  // computed it, says Derived on the bar as it does on the Capital Stack tab.
  // Lineage only knows the figures the engine has written, so a tile marked
  // from lineage alone read the engine's DSCR as a stated one.
  const barMetrics = useMemo<DossierMetric[]>(
    () => metrics.map((metric) => {
      if (metric.derived || metric.implied || metric.sponsor) return metric
      const held = effectiveValue(metric.id)
      if (held !== null && held !== undefined && held !== '') return metric
      const computed = calculatedFrom(calculations.data?.derived)(metric.id)
      if (!computed) return metric
      return { ...metric, derived: (computed.formula ?? 'computed from the deal’s figures').replace(/_/g, ' ') }
    }),
    [metrics, effectiveValue, calculations.data],
  )
  useProvideDossierMetrics('economics', barMetrics)

  return (
    <div data-testid="economics-dashboard" className="flex h-full min-h-0 shrink-0 flex-col gap-2 overflow-hidden">
      {/* Rows SHARE the height when there is enough of it and keep a readable
          minimum when there is not, at which point the dashboard scrolls. It
          used to be a hard `overflow-hidden` over fractional rows, so on any
          screen shorter than the design a four-row financing summary showed
          three rows and the fourth was not merely below the fold — it was
          unreachable. The fourth track is the sponsor's statement, which was
          landing in an implicit auto row and being clipped by that same rule. */}
      <div className="dossier-economics-grid min-h-0 flex-1 space-y-4 overflow-y-auto pb-4">
        <div data-economics-row="top" className="grid gap-4 xl:min-h-0 xl:grid-cols-2">
          <DossierPanel
            title={cashFlowSummaryTitle(model)}
            dense
            className="min-h-[252px] min-w-0 xl:min-h-0"
          >
            <CashFlowSummaryContent model={model} lineage={lineageQuery.data?.fields ?? []} />
          </DossierPanel>

          <DossierPanel title="Income & Expense Trend" dense className="min-h-[252px] min-w-0 xl:min-h-0">
            <FinancialChart rows={model.trend} series={[{ key: 'egi', label: 'Effective gross income', color: '#efc568' }, { key: 'opex', label: 'Operating expenses', color: '#94a3b8' }, { key: 'noi', label: 'Net operating income', color: '#65d5a2' }]} source={model.projectionSource ? `${model.projectionSource.workbook} · ${model.projectionSource.sheet}` : 'Recorded operating schedule'} />
          </DossierPanel>

          <DossierPanel title="Financing Summary" dense className="min-h-[252px] min-w-0 xl:min-h-0">
            <dl className="space-y-1 text-[10px]">
              {model.financing.map((row) => {
                const editors = FINANCING_EDITORS[row.label]
                // A value held off the deal for a person (a later email's, or
                // the lender's own draft) says so under the row; it is never
                // the row's value and never counts as one.
                const held = row.held ? (
                  <div data-held-revision className="-mt-0.5 text-right text-[9px] italic leading-tight text-amber-200/60" title="Decided in the record's Email Thread panel — see the pending-review strip above">
                    {row.held}
                  </div>
                ) : null
                const shown = financingDisplay(row)
                // Unlocked, the row keeps its label and right-aligned value and
                // the value becomes the input — it used to switch to a
                // 118px-label row layout, so unlocking re-flowed the panel.
                // Every row that names a field takes part, not only the
                // listed terms: LTC, the debt yields and the stabilized LTV
                // are inputs when a document states them and open their own
                // inputs when the engine derives them (R1, by value state, as
                // the Audit tab and the server decide it).
                const field: SummaryField | undefined = editors
                  ? { name: editors[0].name, label: editors[0].label, fields: editors }
                  : row.fieldName
                    ? { name: row.fieldName, label: row.label }
                    : undefined
                return (
                  <div key={row.label}>
                    <div className="flex justify-between gap-2">
                      <dt className="shrink-0 text-stone-500">{row.label}</dt>
                      <StatedValue value={shown} source={shown === '—' ? null : stateOf(row.fieldName)} field={field} />
                    </div>
                    {held}
                  </div>
                )
              })}
            </dl>
            {/* Coverage, each figure with its basis — the platform's interest-only
                coverage on the lender's Actual/360 basis, the broker's own
                stated figure, and (a hotel) the figure after the entered FF&E
                reserve. A dash names what the figure is waiting on (WP-G). */}
            <div data-testid="debt-coverage" className="mt-2 border-t border-white/[0.05] pt-1.5">
              <div className="mb-1 font-mono text-[8px] uppercase tracking-wider text-stone-500">Debt service coverage</div>
              <dl className="space-y-1.5 text-[10px]">
                {model.coverage.map((row) => {
                  const fieldName = coverageCatalogueField(row.id, effectiveValue)
                  return (
                  <div key={row.id} data-coverage={row.id}>
                    <div className="flex justify-between gap-2">
                      <dt className="shrink-0 text-stone-500">{row.label}</dt>
                      <StatedValue
                        value={formatMultiple(row.value)}
                        source={row.value == null ? null : stateOf(fieldName)}
                        field={
                          fieldName
                            ? {
                                name: fieldName,
                                label: row.label,
                                // The platform's coverage, computed from the
                                // figures shown, is never typed over (R1) —
                                // blank, it opens the inputs it is waiting on.
                                computed: row.id === 'dscr_io' || row.id === 'dscr_io_stabilized'
                                  ? 'NOI over interest-only debt service (principal × rate × day count)'
                                  : undefined,
                              }
                            : undefined
                        }
                      />
                    </div>
                    <p className="text-[8.5px] leading-snug text-stone-600" title={row.basis}>{row.basis}</p>
                  </div>
                  )
                })}
              </dl>
            </div>
            {/* The cap rate, value, NOI and debt yield a broker stated, kept as
                stated, with the platform's own figure and the variance beside
                each (Gate 0 D1). */}
            <StatedFiguresBlock figures={lineageQuery.data?.statedFigures} className="mt-2" />
            <PanelLink label="View Capital Stack" onClick={gotoLink('capital_stack')} />
          </DossierPanel>

          <DossierPanel title="Pricing & Returns" dense className="min-h-[252px] min-w-0 xl:min-h-0">
            <div className="grid grid-cols-2 gap-1 rounded-md bg-white/[0.025] p-0.5">
              {([['inplace', 'In-Place'], ['stabilized', 'Stabilized']] as const).map(([id, label]) => (
                <button key={id} type="button" onClick={() => setReturnsMode(id)} className={`rounded px-2 py-1 text-[9px] uppercase tracking-wider ${returnsMode === id ? 'border border-amber-300/20 bg-amber-300/[0.06] text-amber-200' : 'text-stone-600 hover:text-stone-300'}`}>
                  {label}
                </button>
              ))}
            </div>
            <CashOnCashGauge value={currentCashOnCash} />
            <dl className="space-y-1 text-[9px]">
              {([
                ['IRR (Levered)', model.returns.leveredIrr, formatPct(model.returns.leveredIrr), 'levered_irr'],
                ['IRR (Unlevered)', model.returns.unleveredIrr, formatPct(model.returns.unleveredIrr), 'unlevered_irr'],
                ['Equity Multiple', model.returns.equityMultiple, formatMultiple(model.returns.equityMultiple), 'equity_multiple'],
                [model.capRateLabel, model.returns.capInplace, formatPct(model.returns.capInplace), 'cap_rate'],
                ['Cap Rate (Stabilized)', model.returns.capStabilized, formatPct(model.returns.capStabilized), 'cap_rate_stabilized', model.capStabilizedDerived],
              ] as const).map(([label, figure, value, fieldName, pageFormula]) => (
                <div key={label} className="flex justify-between gap-2">
                  <dt className="shrink-0 text-stone-500">{label}</dt>
                  <StatedValue
                    value={value}
                    source={figure == null ? null : stateOf(fieldName, pageFormula)}
                    field={{ name: fieldName, label }}
                  />
                </div>
              ))}
            </dl>
            {returnsMode === 'stabilized' && currentCashOnCash == null ? <p className="mt-1 text-[8px] text-stone-600">No stabilized cash-on-cash return is recorded.</p> : null}
            <PanelLink label="View Returns Analysis" onClick={gotoLink('underwriting')} />
          </DossierPanel>
        </div>

        <div data-economics-row="middle" className="grid gap-4 xl:min-h-0 xl:grid-cols-12">
          <DossierPanel title="Expense Breakdown (In-Place)" dense className="min-h-[236px] min-w-0 xl:col-span-4 xl:min-h-0">
            {expenseData.length === 0 ? (
              <ChartEmpty minHeight={172}>
                {model.expenseTotal != null ? `Total operating expenses are ${moneyFull(model.expenseTotal)}, but no category schedule is recorded.` : 'No expense category schedule is recorded.'}
              </ChartEmpty>
            ) : (
              <div className="flex flex-wrap items-center gap-4 py-3">
                <div className="relative h-40 w-40 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={expenseData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="64%" outerRadius="90%" paddingAngle={2} stroke="transparent" isAnimationActive={false}>
                        {expenseData.map((expense, index) => <Cell key={expense.name} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value) => moneyFull(Number(value))} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="font-mono text-[14px] font-semibold text-stone-100">{formatAmount(model.expenseTotal)}</span>
                    <span className="text-[8px] text-stone-400">Total expenses</span>
                  </div>
                </div>
                <ul className="min-w-[140px] flex-1 space-y-3">
                  {model.expenses.map((row, index) => <li key={row.name} className="flex items-start gap-2 text-xs"><span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{background: EXPENSE_COLORS[index % EXPENSE_COLORS.length]}} /><span className="min-w-0 flex-1 text-stone-300">{row.name}</span><span className="shrink-0 font-mono text-stone-100">{model.expenseTotal ? `${(row.amount / model.expenseTotal * 100).toFixed(1)}%` : '—'}</span></li>)}
                </ul>
              </div>
            )}
            {expenseData.length > 0 && <details className="mt-3 text-xs text-stone-300"><summary className="cursor-pointer text-amber-200">Underlying expense data</summary><div className="overflow-x-auto"><table className="mt-2 w-full"><thead><tr><th className="text-left">Category</th><th className="text-right">Amount</th><th className="text-right">Share</th></tr></thead><tbody>{model.expenses.map(row => <tr key={row.name}><td>{row.name}</td><td className="text-right">{new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0}).format(row.amount)}</td><td className="text-right">{model.expenseTotal ? `${(row.amount / model.expenseTotal * 100).toFixed(1)}%` : '—'}</td></tr>)}</tbody></table></div></details>}
            <PanelLink label="View Expense Detail" onClick={gotoLink('underwriting')} />
          </DossierPanel>

          <DossierPanel title="Cash Flow Projection" dense className="min-h-[236px] min-w-0 xl:col-span-4 xl:min-h-0">
            {/* Whose projection this is. The client screens on the sponsor's
                pro forma and underwrites separately, so a table shown without
                saying whose figures they are reads as the desk's own — and
                the cell reference is what lets a reader check the number on
                this screen against the number in the attachment. */}
            {model.projectionSource && (
              <div
                className="mb-1 truncate font-mono text-[8px] uppercase tracking-widest text-amber-200/60"
                title={`The sponsor's figures, from ${model.projectionSource.workbook} at ${model.projectionSource.sheet}`}
              >
                Sponsor · {model.projectionSource.workbook} · {model.projectionSource.sheet}
              </div>
            )}
            {model.projections.length === 0 ? (
              <ChartEmpty minHeight={172}>No recorded multi-year cash-flow projection.</ChartEmpty>
            ) : (
              <div data-panel-scroll className="max-h-[184px] overflow-auto">
                {/* Every column and its header centered, so each header sits over
                    its values (`!` beats the shell's thead rule). */}
                <table data-panel-table className="w-full min-w-[470px] text-center text-[9px]">
                  <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[8px] uppercase tracking-wider text-stone-600">
                    <tr>
                      <th className="px-1 py-1 !text-center font-medium">Year</th>
                      <th className="px-1 py-1 !text-center font-medium">EGI</th>
                      <th className="px-1 py-1 !text-center font-medium">NOI</th>
                      <th className="px-1 py-1 !text-center font-medium">CFADS</th>
                      <th className="px-1 py-1 !text-center font-medium">DSCR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.projections.map((row) => (
                      <tr key={row.year} className="border-t border-white/[0.04] text-stone-300">
                        <td className="px-1 py-0.5 text-center font-mono">{row.year}</td>
                        <td className="px-1 py-0.5 text-center font-mono">{moneyFull(row.egi)}</td>
                        <td className="px-1 py-0.5 text-center font-mono">{moneyFull(row.noi)}</td>
                        <td className="px-1 py-0.5 text-center font-mono">{moneyFull(row.cashFlow)}</td>
                        <td className="px-1 py-0.5 text-center font-mono">{formatMultiple(row.dscr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <PanelLink label="View Full Cash Flow" onClick={gotoLink('underwriting')} />
          </DossierPanel>

          <DossierPanel title="Scenario Analysis" dense className="min-h-[236px] min-w-0 xl:col-span-4 xl:min-h-0">
            <div className="grid grid-cols-3 gap-1 rounded-md bg-white/[0.025] p-0.5">
              {scenarioIds.map((id) => (
                <button key={id} type="button" onClick={() => setSelectedScenario(id)} className={`rounded px-1.5 py-0.5 text-[8px] uppercase tracking-wider ${selectedScenario === id ? 'border border-amber-300/20 bg-amber-300/[0.06] text-amber-200' : 'text-stone-600 hover:text-stone-300'}`}>
                  {model.scenarios[id]?.label ?? (id === 'base' ? 'Base Case' : id === 'downside' ? 'Downside' : 'Upside')}
                </button>
              ))}
            </div>
            <div className="mt-1 shrink-0 overflow-x-auto">
              <table data-panel-table className="w-full min-w-[390px] text-[9px] leading-tight">
                <thead className="font-mono text-[8px] uppercase tracking-wider text-stone-600">
                  <tr>
                    {/* `!` beats the shell's [&_thead_th]:text-center, so each
                        header sits over its right-aligned values (nd3-30). */}
                    <th className="py-0.5 !text-left font-medium">Metric</th>
                    {scenarioIds.map((id) => <th key={id} className={`py-0.5 !text-right font-medium ${selectedScenario === id ? 'text-amber-300' : ''}`}>{id}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {scenarioRows.map((row) => (
                    <tr key={row.label} className="border-t border-white/[0.04]">
                      <td className="py-0.5 text-stone-500">{row.label}</td>
                      {scenarioIds.map((id) => <td key={id} className={`py-0.5 text-right font-mono ${selectedScenario === id ? 'text-stone-100' : 'text-stone-400'}`}>{scenarioValue(model.scenarios[id], row)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-[8px] leading-snug text-stone-600">{model.scenarioDisclosure}</p>
            <PanelLink label="View Full Scenario Analysis" onClick={gotoLink('underwriting')} />
          </DossierPanel>
        </div>

        <div data-economics-row="bottom" className="grid gap-4 xl:min-h-0 xl:grid-cols-2">
          <DossierPanel title="Debt Coverage Ratio Trend" dense className="min-h-[164px] min-w-0 xl:min-h-0">
            <FinancialChart rows={model.dscrSeries} series={[{ key: 'dscr', label: 'DSCR', color: '#efc568' }]} unit="multiple" source="Recorded debt coverage schedule" />
          </DossierPanel>

          <DossierPanel title="Cap Rate Analysis" dense className="min-h-[164px] min-w-0 xl:min-h-0">
            <dl className="space-y-2 pt-0.5 text-[10px]">
              {([
                [model.capRateLabel, model.capRates.inplace, 'cap_rate'],
                ['Stabilized Cap Rate', model.capRates.stabilized, 'cap_rate_stabilized', model.capStabilizedDerived],
                // Market data, not a catalogue field: the blob names its source.
                ['Market Cap Rate', model.capRates.market, undefined],
                ['Terminal Cap Rate', model.capRates.terminal, 'exit_cap'],
              ] as const).map(([label, value, fieldName, pageFormula]) => (
                <div key={label} className="flex justify-between gap-2">
                  <dt className="shrink-0 text-stone-500">{label}</dt>
                  <StatedValue
                    className="text-stone-100"
                    source={value == null ? null : stateOf(fieldName, pageFormula)}
                    field={fieldName ? { name: fieldName, label } : undefined}
                    value={
                      /* A market figure needs a market source; the deal's own
                         cap rate is never shown under this label. */
                      label === 'Market Cap Rate' && value == null ? (
                        <span className="text-stone-500">No market source</span>
                      ) : formatPct(value)
                    }
                  />
                </div>
              ))}
            </dl>
          </DossierPanel>

          <DossierPanel title="Sensitivity" dense className="min-h-[164px] min-w-0 xl:min-h-0">
            {model.sensitivity.length === 0 ? (
              <ChartEmpty minHeight={102}>No saved sensitivity schedule is recorded. No ranges or outputs were inferred.</ChartEmpty>
            ) : (
              <div data-panel-scroll className="max-h-[108px] space-y-2 overflow-y-auto pr-1">
                {model.sensitivity.map((row) => {
                  const hasRange = row.low != null && row.base != null && row.high != null && row.high > row.low
                  return (
                    <div key={row.id} className="grid min-w-0 grid-cols-[92px_minmax(0,1fr)_72px] items-center gap-2 text-[9px]">
                      <div className="min-w-0">
                        <div className="truncate text-stone-400">{row.label}</div>
                        <div className="font-mono text-[8px] text-stone-600">{sensitivityValue(row, row.base)}</div>
                      </div>
                      {hasRange ? (
                        <div className="min-w-0">
                          <input type="range" min={row.low} max={row.high} value={row.base} readOnly aria-label={`${row.label} recorded sensitivity range`} className="w-full accent-amber-400" />
                          <div className="flex justify-between font-mono text-[7px] text-stone-600"><span>{sensitivityValue(row, row.low)}</span><span>{sensitivityValue(row, row.high)}</span></div>
                        </div>
                      ) : (
                        <div className="text-center text-[8px] text-stone-600">Range not recorded</div>
                      )}
                      <div className="text-right">
                        <div className="truncate text-[7px] uppercase tracking-wider text-stone-600">{row.resultLabel ?? 'Result'}</div>
                        <div className="font-mono text-[10px] text-stone-200">{sensitivityResultValue(row)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </DossierPanel>

          <DossierPanel title="Recent Activity" dense className="min-h-[164px] min-w-0 xl:min-h-0">
            {model.updates.length === 0 ? (
              <ChartEmpty minHeight={90}>No recent notes or economics activity are recorded.</ChartEmpty>
            ) : (
              <ul data-panel-scroll className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                {model.updates.slice(0, 5).map((update) => (
                  <li key={update.id} className="flex items-start gap-2 text-[9px]">
                    <span className="mt-0.5 text-amber-300/80">{update.id.startsWith('note-') ? <FileText size={10} /> : <Activity size={10} />}</span>
                    {/* Two lines, not three: the time sits beside the title. */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-stone-200">{update.title}</span>
                        <span className="shrink-0 font-mono text-[7px] text-stone-600">{when(update.at)}</span>
                      </div>
                      <div className="truncate text-[8px] text-stone-500">{update.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <PanelLink label="View All Activity" onClick={gotoLink('audit')} />
          </DossierPanel>
        </div>

      </div>
    </div>
  )
}

/**
 * The sponsor's operating statement, in full and in their own words.
 *
 * The canonical seven keys the charts consume name five of the thirty-one
 * labelled lines in Nyala's Annual_Summary, three of twenty-six in Lakeside's
 * pro forma. `GTL/(LTL)`, `RUBS`, `Absorption & Turnover Vacancy`, `CAM
 * Shortfall (memo: already in NOI)` are not noise around the summary — they
 * are the reason somebody opens the model. Every figure carries the cell it
 * was read from, so a number here can be checked against the attachment.
 */
export function SponsorStatementPanel({ statement, fill = false }: { statement: SponsorStatement; /** Own tab: full height, every line. */ fill?: boolean }) {
  const [expanded, setExpanded] = useState(fill)
  const [view, setView] = useState<'table' | 'chart'>('table')
  const CADENCE: Record<string, string> = {
    annual: 'annual', monthly: 'monthly', quarterly: 'quarterly', unknown: 'periodic',
  }
  const COLLAPSED = 12
  const shown = expanded ? statement.lines : statement.lines.slice(0, COLLAPSED)
  const hidden = statement.lines.length - shown.length
  return (
    <DossierPanel
      title="Sponsor's Operating Statement"
      dense
      className={fill ? 'h-full min-h-0 min-w-0' : 'min-h-[236px] min-w-0 xl:col-span-8 xl:min-h-0'}
    >
      <div
        className="mb-1 truncate font-mono text-[8px] uppercase tracking-widest text-amber-200/60"
        title={`${statement.lines.length} lines from ${statement.workbook} at ${statement.sheet}`}
      >
        Sponsor · {statement.workbook} · {statement.sheet} ·{' '}
        {statement.lines.length} lines · {CADENCE[statement.cadence] ?? statement.cadence}
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[10px]">
        {(['table','chart'] as const).map(mode => <button key={mode} type="button" aria-pressed={view === mode} onClick={() => setView(mode)} className={`rounded border px-2 py-1 ${view === mode ? 'border-amber-300/30 bg-amber-300/[0.06] text-amber-200' : 'border-stone-800 text-stone-400'}`}>{mode === 'table' ? 'Table' : 'Chart'}</button>)}
      </div>
      {view === 'chart' ? <div className={fill ? 'min-h-0 flex-1 overflow-auto' : ''}>
        <SponsorStatementChart statement={statement} fill={fill} />
      </div> : <>
      <div data-panel-scroll className={fill ? 'min-h-0 flex-1 overflow-auto' : 'max-h-[220px] overflow-auto'}>
        <table data-panel-table className="w-full min-w-[520px] text-left text-[9px]">
          <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[8px] uppercase tracking-wider text-stone-600">
            <tr>
              <th className="px-1 py-1 font-medium">Line</th>
              {statement.periods.map((period) => (
                <th key={period} className="px-1 py-1 text-right font-medium">{period}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((line) => (
              <tr
                key={`${line.label}-${line.cells[0] ?? ''}`}
                className="border-t border-white/[0.04] text-stone-300"
              >
                {/* A line the app has a name for is the sponsor's statement of
                    a figure the deal also tracks; one it does not is theirs
                    alone. Both are shown; only the first is highlighted. */}
                <td
                  className={`px-1 py-1 capitalize ${line.key ? 'text-amber-100/80' : ''}`}
                  title={line.cells.find(Boolean) ?? undefined}
                >
                  {line.label}
                </td>
                {statement.periods.map((period, index) => (
                  <td
                    key={period}
                    className="px-1 text-right font-mono"
                    title={line.cells[index] || undefined}
                  >
                    {line.values[index] == null ? '—' : formatStatementValue(line.values[index] as number)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
      {view === 'table' && hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 font-mono text-[8px] uppercase tracking-widest text-amber-300/70 hover:text-amber-200"
        >
          Show {hidden} more {hidden === 1 ? 'line' : 'lines'}
        </button>
      )}
      {statement.truncated ? (
        <div className="mt-1 font-mono text-[8px] text-stone-500">
          {statement.truncated} further lines were not stored.
        </div>
      ) : null}
    </DossierPanel>
  )
}

/** A statement holds dollars, ratios and percentages in one column, and the
 *  model does not say which is which. Scale is the only honest signal. */
function formatStatementValue(value: number): string {
  const magnitude = Math.abs(value)
  if (magnitude === 0) return '0'
  if (magnitude < 10) return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
  if (magnitude < 1000) return value.toFixed(2)
  return Math.round(value).toLocaleString()
}

export default EconomicsPage
